import toPairs from "lodash-es/toPairs";
import React from "react";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { isSoldPollShow } from "@/mk2/apps/bazaar/containers/IsSoldPoll/IsSoldPoll.actions";
import {
    PRODUCT_DETAIL_ACTIVATE_PRODUCT,
    PRODUCT_DETAIL_DEACTIVATE_PRODUCT,
    PRODUCT_DETAIL_DELETE_PRODUCT,
    PRODUCT_DETAIL_LOAD,
    PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS,
    PRODUCT_DETAIL_MARK_OK_PRODUCT,
    PRODUCT_DETAIL_NEW_MESSAGE,
    PRODUCT_DETAIL_NEW_REPLY,
    PRODUCT_DETAIL_NEW_REVIEW,
    PRODUCT_DETAIL_READ_MESSAGES,
    PRODUCT_DETAIL_REMOVE_BUYER,
    PRODUCT_DETAIL_REPOST_PRODUCT,
    PRODUCT_DETAIL_RESTORE_BUYER,
    PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE,
    PRODUCT_DETAIL_UNDELETE_PRODUCT,
    PRODUCT_DETAIL_VIEW,
    ProductDetailActivateProductAction,
    ProductDetailActivateProductApiNormalizedResponse,
    ProductDetailDeactivateProductAction,
    ProductDetailDeactivateProductApiNormalizedResponse,
    ProductDetailDeleteProductAction,
    ProductDetailDeleteProductApiNormalizedResponse,
    ProductDetailLoadAction,
    ProductDetailLoadApiNormalizedResponse,
    ProductDetailLoadRelatedProductsAction,
    ProductDetailLoadRelatedProductsApiNormalizedResponse,
    ProductDetailMarkOKProductAction,
    ProductDetailMarkOKProductApiNormalizedResponse,
    ProductDetailNewMessageAction,
    ProductDetailNewMessageApiNormalizedResponse,
    ProductDetailNewReplyAction,
    ProductDetailNewReplyApiNormalizedResponse,
    ProductDetailNewReviewAction,
    ProductDetailNewReviewApiNormalizedResponse,
    ProductDetailReadMessagesAction,
    ProductDetailRemoveBuyerAction,
    ProductDetailRemoveBuyerApiNormalizedResponse,
    ProductDetailRepostProductAction,
    ProductDetailRepostProductApiNormalizedResponse,
    ProductDetailRestoreBuyerAction,
    ProductDetailRestoreBuyerApiNormalizedResponse,
    ProductDetailSetShowSellerPhoneAction,
    ProductDetailSetShowSellerPhoneApiNormalizedResponse,
    ProductDetailUndeleteProductAction,
    ProductDetailUndeleteProductApiNormalizedResponse,
    ProductDetailViewAction,
    ProductDetailViewApiNormalizedResponse,
    productDetailActivateProductApi,
    productDetailDeactivateProductApi,
    productDetailDeleteProductApi,
    productDetailLoadApi,
    productDetailLoadRelatedProductsApi,
    productDetailMarkOKProductApi,
    productDetailNewMessageApi,
    productDetailNewReplyApi,
    productDetailNewReviewApi,
    productDetailReadMessagesApi,
    productDetailRemoveBuyerApi,
    productDetailRepostProductApi,
    productDetailRestoreBuyerApi,
    productDetailSetShowSellerPhoneApi,
    productDetailUndeleteProductApi,
    productDetailViewApi,
} from "@/mk2/apps/bazaar/containers/ProductDetail/ProductDetail.actions";
import { CONTACT_SELLER_FORM, MESSAGE_REPLY_FORM, REVIEW_FORM } from "@/mk2/apps/bazaar/helpers";
import { MarkEntity, MarkSchema, ProductSchema, RepostableStatus } from "@/mk2/apps/bazaar/schemas";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { ComplaintSchema, ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import { findComplaintsNonDeleted } from "@/mk2/containers/Complaints/Complaints.selectors";
import {
    XHRAction,
    XHRFormError,
    XHRGetError,
    XHRPostError,
    handleXHRGetErrorSaga,
    handleXHRPostErrorSaga,
    normalizeError,
} from "@/mk2/helpers/api";
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from "@/mk2/helpers/form";
import { PhotoUploadStatus } from "@/mk2/helpers/form.reducers";
import { showErrorToast, showSuccessToast } from "@/mk2/helpers/toasts";
import { getLogger } from "@/mk2/logger";
import { entitiesRemove } from "@/mk2/reducers/entities";
import { findDenormalizedEntitiesByCond } from "@/mk2/selectors";
import { HistoryAction } from "@/mk2/services/browserHistory";
import { interpolate } from "@/mk2/services/i18n";
import {
    BAZAAR_YOU_TURNED_OFF_SHOWING_YOUR_NUMBER,
    BAZAAR_YOU_TURNED_ON_SHOWING_YOUR_NUMBER,
    NOT_REPOSTABLE,
    REALLY_WANT_TO_REMOVE_BUYER,
    REPOSTS_OVER_LIMIT_count_COULD_NOT_BE_REPOSTED,
    THERE_WERE_ERRORS_WHILE_REPOSTING,
    THE_ENTRY_WAS_ACTIVATED,
    THE_ENTRY_WAS_DEACTIVATED,
    THE_ENTRY_title_WAS_DELETED,
    THE_ENTRY_title_WAS_UNDELETED,
    YOUR_ENTRY_title_DOES_NOT_HAVE_UPDATED_PROPERTIES_PLEASE_FILL_THEM_OUT_PRIOR_TO_REPOSTING_IT,
    count_SELECTED_ENTRIES_ARE_REPOSTING,
} from "@/mk/autogenerated/translations/ProductDetail.sagas.d462562607599e569b2ff4c58b2fa543";
import { productSlugToArray } from "@/mk/bazaar/product/utils";
import {
    bazaar_api_v3_buyers_remove_url,
    bazaar_api_v3_buyers_restore_url,
    bazaar_api_v3_messages_new_message_url,
    bazaar_api_v3_messages_read_url,
    bazaar_api_v3_messages_reply_url,
    bazaar_api_v3_product_activate_url,
    bazaar_api_v3_product_deactivate_url,
    bazaar_api_v3_product_delete_url,
    bazaar_api_v3_product_detail_url,
    bazaar_api_v3_product_ok_url,
    bazaar_api_v3_product_related_products_url,
    bazaar_api_v3_product_undelete_url,
    bazaar_api_v3_product_view_url,
    bazaar_api_v3_products_repost_url,
    bazaar_api_v3_reviews_new_url,
    bazaar_api_v3_seller_set_show_phone_url,
    bazaar_single_page_modify_product_url,
} from "@/mk/urls/functions";

const logger = getLogger("bazaar/ProductDetail.sagas");

interface LoadApiResponse {
    body: {
        sellerPhone: string;
        entities: ProductDetailLoadApiNormalizedResponse["entities"];
        result: ProductDetailLoadApiNormalizedResponse["result"];
        viewsCount: number;
        historyCount: number;
    };
}

function* load({
    xhr,
    slug,
    location,
    routeNavigate,
}: ProductDetailLoadAction & XHRAction & HistoryAction) {
    yield put(productDetailLoadApi.request(slug));

    try {
        const response: LoadApiResponse = yield call(() =>
            xhr.get(bazaar_api_v3_product_detail_url(slug)),
        );

        yield put(
            productDetailLoadApi.success(
                slug,
                response.body.viewsCount,
                response.body.historyCount,
                response.body.sellerPhone,
                {
                    entities: response.body.entities,
                    result: response.body.result,
                },
            ),
        );

        // Remove complaints and marks from entities cache if server do not send them
        const marks = yield select((state) =>
            findDenormalizedEntitiesByCond<MarkEntity>(
                state,
                MarkSchema,
                `cacheKey-EditProduct-sagas-${slug}-marks`,
                (sel) => sel.targetId === response.body.result.product,
            ),
        );
        const complaints = yield select((state) =>
            findComplaintsNonDeleted(
                state,
                ComplaintTargetType.BazaarProduct,
                response.body.result.product,
            ),
        );

        if (marks?.length || complaints?.length) {
            yield put(
                entitiesRemove({
                    [MarkSchema.key]: marks
                        ? marks
                              .map((m) => m.id)
                              .filter((id) => !response.body.result.marks.includes(id))
                        : [],
                    [ComplaintSchema.key]: complaints
                        ? complaints
                              .map((c) => c.id)
                              .filter((id) => !response.body.result.complaints.includes(id))
                        : [],
                }),
            );
        }
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, location, routeNavigate, logger);

        // pri zmazanom inzerate, chceme vyrenderovat
        // stranky s podobnymi inzeratmi
        const normalizedResponse =
            error.status === 410
                ? {
                      entities: error.response.body.entities,
                      result: error.response.body.result,
                  }
                : {
                      entities: {},
                      result: {},
                  };
        yield put(productDetailLoadApi.failure(slug, normalizeError(error), normalizedResponse));
    }
}

interface LoadRelatedProductsApiResponse {
    body: {
        entities: ProductDetailLoadRelatedProductsApiNormalizedResponse["entities"];
        result: ProductDetailLoadRelatedProductsApiNormalizedResponse["result"];
        hasNext: boolean;
    };
}

function* loadRelatedProducts({
    xhr,
    slug,
    start,
    location,
    routeNavigate,
}: ProductDetailLoadRelatedProductsAction & XHRAction & HistoryAction) {
    yield put(productDetailLoadRelatedProductsApi.request(slug, start));

    try {
        const response: LoadRelatedProductsApiResponse = yield call(() =>
            xhr.get(bazaar_api_v3_product_related_products_url(slug), {
                start,
            }),
        );

        yield put(
            productDetailLoadRelatedProductsApi.success(slug, start, response.body.hasNext, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, location, routeNavigate, logger);
        yield put(productDetailLoadRelatedProductsApi.failure(slug, start, normalizeError(error)));
    }
}

interface ActivateApiResponse {
    body: {
        entities: ProductDetailActivateProductApiNormalizedResponse["entities"];
        result: ProductDetailActivateProductApiNormalizedResponse["result"];
    };
}

function* activateProduct({
    xhr,
    productId,
    location,
    routeNavigate,
}: ProductDetailActivateProductAction & XHRAction & HistoryAction) {
    yield put(productDetailActivateProductApi.request(productId));

    try {
        const response: ActivateApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_product_activate_url(productId)),
        );

        yield put(
            productDetailActivateProductApi.success(productId, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
        yield showSuccessToast(THE_ENTRY_WAS_ACTIVATED);
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, location, routeNavigate, logger);
        yield put(productDetailActivateProductApi.failure(productId, normalizeError(error)));
    }
}

interface DeactivateApiResponse {
    body: {
        entities: ProductDetailDeactivateProductApiNormalizedResponse["entities"];
        result: ProductDetailDeactivateProductApiNormalizedResponse["result"];
    };
}

function* deactivateProduct({
    xhr,
    productId,
    location,
    routeNavigate,
}: ProductDetailDeactivateProductAction & XHRAction & HistoryAction) {
    yield put(productDetailDeactivateProductApi.request(productId));

    try {
        const response: DeactivateApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_product_deactivate_url(productId)),
        );

        yield put(
            productDetailDeactivateProductApi.success(productId, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
        yield showSuccessToast(THE_ENTRY_WAS_DEACTIVATED);
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, location, routeNavigate, logger);
        yield put(productDetailDeactivateProductApi.failure(productId, normalizeError(error)));
    }
}

interface RepostErrors {
    [type: string]: Array<{
        id: number;
        title: string;
        slug: string;
    }>;
}

interface RepostApiResponse {
    body: {
        entities: ProductDetailRepostProductApiNormalizedResponse["entities"];
        result: ProductDetailRepostProductApiNormalizedResponse["result"];
        count: number;
        errors: RepostErrors;
    };
}

function* displayErrors(errors: RepostErrors) {
    for (const [error, products] of toPairs(errors)) {
        switch (error) {
            case RepostableStatus.REPOSTABLE_NOT_AVAILABLE:
                yield showErrorToast(
                    interpolate(REPOSTS_OVER_LIMIT_count_COULD_NOT_BE_REPOSTED, {
                        count: products.length,
                    }),
                );
                break;
            case RepostableStatus.REPOSTING_FAILED:
                yield showErrorToast(THERE_WERE_ERRORS_WHILE_REPOSTING);
                break;
            case RepostableStatus.REPOSTABLE_ENTER_FACETS:
                yield showErrorToast(
                    <Interpolate
                        i18nKey={
                            YOUR_ENTRY_title_DOES_NOT_HAVE_UPDATED_PROPERTIES_PLEASE_FILL_THEM_OUT_PRIOR_TO_REPOSTING_IT
                        }
                        title={
                            <Link
                                to={bazaar_single_page_modify_product_url(
                                    ...productSlugToArray(products[0].slug),
                                )}
                            >
                                {products[0].title}
                            </Link>
                        }
                    />,
                );
                break;
            case RepostableStatus.NOT_REPOSTABLE:
                yield showErrorToast(NOT_REPOSTABLE);
                break;
        }
    }
}

function* repostProduct({
    xhr,
    productId,
    location,
    routeNavigate,
}: ProductDetailRepostProductAction & XHRAction & HistoryAction) {
    yield put(productDetailRepostProductApi.request(productId));

    try {
        const response: RepostApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_products_repost_url(), {
                productIds: [productId],
            }),
        );

        const count = response.body.count;
        yield put(
            productDetailRepostProductApi.success(productId, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );

        if (count > 0) {
            yield showSuccessToast(interpolate(count_SELECTED_ENTRIES_ARE_REPOSTING, { count }));
        }

        yield displayErrors(response.body.errors);
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, location, routeNavigate, logger);
        yield put(productDetailRepostProductApi.failure(productId, normalizeError(error)));
    }
}

interface DeleteApiResponse {
    body: {
        entities: ProductDetailDeleteProductApiNormalizedResponse["entities"];
        result: ProductDetailDeleteProductApiNormalizedResponse["result"];
    };
}

function* deleteProduct({
    xhr,
    productId,
    reason,
    location,
    routeNavigate,
}: ProductDetailDeleteProductAction & XHRAction & HistoryAction) {
    yield put(productDetailDeleteProductApi.request(productId, reason));

    try {
        const response: DeleteApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_product_delete_url(productId), {
                reason,
            }),
        );

        yield put(
            productDetailDeleteProductApi.success(productId, reason, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
        yield showSuccessToast(
            interpolate(THE_ENTRY_title_WAS_DELETED, {
                title: response.body.entities[ProductSchema.key][response.body.result.product]
                    .title,
            }),
        );

        // Only 10% change to display poll
        if (Math.random() < 0.1 && !reason) {
            yield put(isSoldPollShow(productId));
        }
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, location, routeNavigate, logger);
        yield put(productDetailDeleteProductApi.failure(productId, reason, normalizeError(error)));
    }
}

interface UndeleteApiResponse {
    body: {
        entities: ProductDetailUndeleteProductApiNormalizedResponse["entities"];
        result: ProductDetailUndeleteProductApiNormalizedResponse["result"];
    };
}

function* undeleteProduct({
    xhr,
    productId,
    location,
    routeNavigate,
}: ProductDetailUndeleteProductAction & XHRAction & HistoryAction) {
    yield put(productDetailUndeleteProductApi.request(productId));

    try {
        const response: UndeleteApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_product_undelete_url(productId)),
        );

        yield put(
            productDetailUndeleteProductApi.success(productId, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
        yield showSuccessToast(
            interpolate(THE_ENTRY_title_WAS_UNDELETED, {
                title: response.body.entities[ProductSchema.key][response.body.result.product]
                    .title,
            }),
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, location, routeNavigate, logger);
        yield put(productDetailUndeleteProductApi.failure(productId, normalizeError(error)));
    }
}

interface MarkOKApiResponse {
    body: {
        entities: ProductDetailMarkOKProductApiNormalizedResponse["entities"];
        result: ProductDetailMarkOKProductApiNormalizedResponse["result"];
    };
}

function* markOKProduct({
    xhr,
    productId,
    location,
    routeNavigate,
}: ProductDetailMarkOKProductAction & XHRAction & HistoryAction) {
    yield put(productDetailMarkOKProductApi.request(productId));

    try {
        const response: MarkOKApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_product_ok_url(productId)),
        );

        yield put(
            productDetailMarkOKProductApi.success(productId, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, location, routeNavigate, logger);
        yield put(productDetailMarkOKProductApi.failure(productId, normalizeError(error)));
    }
}

interface NewMessageApiResponse {
    body: {
        entities: ProductDetailNewMessageApiNormalizedResponse["entities"];
        result: ProductDetailNewMessageApiNormalizedResponse["result"];
    };
}

function* newMessage({
    productId,
    slug,
    data,
    xhr,
    location,
    routeNavigate,
}: ProductDetailNewMessageAction & XHRAction & HistoryAction) {
    yield put(productDetailNewMessageApi.request(productId, slug, data));

    const formName = CONTACT_SELLER_FORM(productId);

    try {
        const response: NewMessageApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_messages_new_message_url(productId), {
                ...data,
                photos: data.photos
                    ? data.photos.filter((photo) => photo.status === PhotoUploadStatus.SUCCESS)
                    : [],
            }),
        );

        yield handleXHRFormSuccessSaga(formName, routeNavigate);
        yield put(
            productDetailNewMessageApi.success(productId, slug, data, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRFormErrorSaga(formName, error as XHRFormError, logger);
        yield put(productDetailNewMessageApi.failure(productId, slug, data, normalizeError(error)));
    }
}

interface ReadApiResponse {
    body: {
        read: number; // number of read reviews
    };
}

function* readMessages({
    messagesIds,
    xhr,
    location,
    routeNavigate,
}: ProductDetailReadMessagesAction & XHRAction & HistoryAction) {
    yield put(productDetailReadMessagesApi.request(messagesIds));

    try {
        const response: ReadApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_messages_read_url(), {
                messagesIds,
            }),
        );

        yield put(
            productDetailReadMessagesApi.success(messagesIds, {
                result: null,
                entities: {},
            }),
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, location, routeNavigate, logger);
        yield put(productDetailReadMessagesApi.failure(messagesIds, normalizeError(error)));
    }
}

interface NewReviewApiResponse {
    body: {
        entities: ProductDetailNewReviewApiNormalizedResponse["entities"];
        result: ProductDetailNewReviewApiNormalizedResponse["result"];
    };
}

function* newReview({
    buyerId,
    slug,
    data,
    xhr,
    location,
    routeNavigate,
}: ProductDetailNewReviewAction & XHRAction & HistoryAction) {
    yield put(productDetailNewReviewApi.request(buyerId, slug, data));

    const formName = REVIEW_FORM(buyerId);

    try {
        const response: NewReviewApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_reviews_new_url(buyerId), {
                ...data,
                photos: data.photos
                    ? data.photos.filter((photo) => photo.status === PhotoUploadStatus.SUCCESS)
                    : [],
            }),
        );

        yield handleXHRFormSuccessSaga(formName, routeNavigate);
        yield put(
            productDetailNewReviewApi.success(buyerId, slug, data, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRFormErrorSaga(formName, error as XHRFormError, logger);
        yield put(productDetailNewReviewApi.failure(buyerId, slug, data, normalizeError(error)));
    }
}

interface NewReplyApiResponse {
    body: {
        entities: ProductDetailNewReplyApiNormalizedResponse["entities"];
        result: ProductDetailNewReplyApiNormalizedResponse["result"];
    };
}

function* newReply({
    buyerId,
    slug,
    data,
    xhr,
    location,
    routeNavigate,
}: ProductDetailNewReplyAction & XHRAction & HistoryAction) {
    yield put(productDetailNewReplyApi.request(buyerId, slug, data));

    const formName = MESSAGE_REPLY_FORM(buyerId);

    try {
        const response: NewReplyApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_messages_reply_url(buyerId), {
                ...data,
                photos: data.photos
                    ? data.photos.filter((photo) => photo.status === PhotoUploadStatus.SUCCESS)
                    : [],
            }),
        );

        yield handleXHRFormSuccessSaga(formName, routeNavigate);
        yield put(
            productDetailNewReplyApi.success(buyerId, slug, data, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRFormErrorSaga(formName, error as XHRFormError, logger);
        yield put(productDetailNewReplyApi.failure(buyerId, slug, data, normalizeError(error)));
    }
}

interface RemoveBuyerApiResponse {
    body: {
        entities: ProductDetailRemoveBuyerApiNormalizedResponse["entities"];
        result: ProductDetailRemoveBuyerApiNormalizedResponse["result"];
    };
}

function* removeBuyer({
    slug,
    buyerId,
    xhr,
    location,
    routeNavigate,
}: ProductDetailRemoveBuyerAction & XHRAction & HistoryAction) {
    if (!window.confirm(REALLY_WANT_TO_REMOVE_BUYER)) {
        return;
    }

    yield put(productDetailRemoveBuyerApi.request(slug, buyerId));

    try {
        const response: RemoveBuyerApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_buyers_remove_url(buyerId)),
        );

        yield put(
            productDetailRemoveBuyerApi.success(slug, buyerId, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, location, routeNavigate, logger);
        yield put(productDetailRemoveBuyerApi.failure(slug, buyerId, normalizeError(error)));
    }
}

interface RestoreBuyerApiResponse {
    body: {
        entities: ProductDetailRestoreBuyerApiNormalizedResponse["entities"];
        result: ProductDetailRestoreBuyerApiNormalizedResponse["result"];
    };
}

function* restoreBuyer({
    slug,
    buyerId,
    xhr,
    location,
    routeNavigate,
}: ProductDetailRestoreBuyerAction & XHRAction & HistoryAction) {
    yield put(productDetailRestoreBuyerApi.request(slug, buyerId));

    try {
        const response: RestoreBuyerApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_buyers_restore_url(buyerId)),
        );

        yield put(
            productDetailRestoreBuyerApi.success(slug, buyerId, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, location, routeNavigate, logger);
        yield put(productDetailRestoreBuyerApi.failure(slug, buyerId, normalizeError(error)));
    }
}

interface ViewApiResponse {
    body: {
        entities: ProductDetailViewApiNormalizedResponse["entities"];
        result: ProductDetailViewApiNormalizedResponse["result"];
        viewsCount: number;
    };
}

function* viewProduct({
    xhr,
    productId,
    productSlug,
    location,
    routeNavigate,
}: ProductDetailViewAction & XHRAction & HistoryAction) {
    yield put(productDetailViewApi.request(productId, productSlug));

    try {
        const response: ViewApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_product_view_url(productId)),
        );

        yield put(
            productDetailViewApi.success(productId, productSlug, response.body.viewsCount, {
                result: response.body.result,
                entities: response.body.entities,
            }),
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, location, routeNavigate, logger);
        yield put(productDetailViewApi.failure(productId, productSlug, normalizeError(error)));
    }
}

interface ApiResponseProductDetailSetShowSellerPhone {
    body: {
        sellerPhone: string;
        entities: ProductDetailSetShowSellerPhoneApiNormalizedResponse["entities"];
        result: ProductDetailSetShowSellerPhoneApiNormalizedResponse["result"];
    };
}

function* setShowSellerPhone({
    value,
    slug,
    phoneVerified,
    sellerId,
    xhr,
    location,
    routeNavigate,
}: ProductDetailSetShowSellerPhoneAction & XHRAction & HistoryAction) {
    yield put(productDetailSetShowSellerPhoneApi.request(value, slug, phoneVerified, sellerId));

    try {
        const response: ApiResponseProductDetailSetShowSellerPhone = yield call(() =>
            xhr.post(bazaar_api_v3_seller_set_show_phone_url(sellerId), {
                showSellerPhone: value,
            }),
        );

        yield put(
            productDetailSetShowSellerPhoneApi.success(
                value,
                slug,
                phoneVerified,
                sellerId,
                response.body.sellerPhone,
                {
                    entities: response.body.entities,
                    result: response.body.result,
                },
            ),
        );

        yield showSuccessToast(
            <Interpolate
                i18nKey={
                    value
                        ? BAZAAR_YOU_TURNED_ON_SHOWING_YOUR_NUMBER
                        : BAZAAR_YOU_TURNED_OFF_SHOWING_YOUR_NUMBER
                }
                useDangerouslySetInnerHTML
            />,
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, location, routeNavigate, logger);
        yield put(
            productDetailSetShowSellerPhoneApi.failure(
                value,
                slug,
                phoneVerified,
                sellerId,
                normalizeError(error),
            ),
        );
    }
}

export default function* root() {
    yield all([
        takeEvery(PRODUCT_DETAIL_LOAD, load),
        takeEvery(PRODUCT_DETAIL_ACTIVATE_PRODUCT, activateProduct),
        takeEvery(PRODUCT_DETAIL_DEACTIVATE_PRODUCT, deactivateProduct),
        takeEvery(PRODUCT_DETAIL_DELETE_PRODUCT, deleteProduct),
        takeEvery(PRODUCT_DETAIL_UNDELETE_PRODUCT, undeleteProduct),
        takeEvery(PRODUCT_DETAIL_REPOST_PRODUCT, repostProduct),
        takeEvery(PRODUCT_DETAIL_MARK_OK_PRODUCT, markOKProduct),
        takeEvery(PRODUCT_DETAIL_NEW_MESSAGE, newMessage),
        takeEvery(PRODUCT_DETAIL_READ_MESSAGES, readMessages),
        takeEvery(PRODUCT_DETAIL_NEW_REVIEW, newReview),
        takeEvery(PRODUCT_DETAIL_NEW_REPLY, newReply),
        takeEvery(PRODUCT_DETAIL_REMOVE_BUYER, removeBuyer),
        takeEvery(PRODUCT_DETAIL_RESTORE_BUYER, restoreBuyer),
        takeEvery(PRODUCT_DETAIL_VIEW, viewProduct),
        takeEvery(PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE, setShowSellerPhone),
        takeEvery(PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS, loadRelatedProducts),
    ]);
}
